import React, { useId, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Avatar, Box, Checkbox, Chip, CircularProgress } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useQuery } from '@tanstack/react-query';
import { debounce } from '@tenant/utility/helper/Utils';
import jwtAxios from '@tenant/services/auth/jwt-auth';
import API_STRINGS from 'constants/Api';
import PropTypes from 'prop-types';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const FIELD_NAME = 'dockerImageList';
const KEY = 'Id';
const LABEL = 'Name';

const MarketplaceAppSelect = (props) => {
  const {
    error,
    helperText,
    value = [],
    required,
    handleInputChange,
    excludes,
    textFieldProps,
    isMultiple = false,
    queryKey = '',
    limitTags = 2,
    initialData = [],
    options = [],
    ...rest
  } = props;
  const [isEnabled, setIsEnabled] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTermStatic, setSearchTermStatic] = useState('');

  const isStaticOptions = useMemo(() => options?.length > 0 && Array.isArray(options), [options]);

  const currentOptionsStatic = useMemo(() => {
    if (isStaticOptions) {
      return options.filter((option) =>
        option[LABEL].toLowerCase().includes(searchTermStatic.toLowerCase()),
      );
    }
    return [];
  }, [options, searchTermStatic]);

  const { data: apps, isLoading } = useQuery({
    enabled: isStaticOptions ? false : isEnabled,
    queryKey: ['marketplace-app-list-select', queryKey, searchTerm],
    queryFn: () =>
      jwtAxios.get(API_STRINGS.APP_MANAGER.GET_APPS_SELECT, {
        params: { searchTerm },
      }),
  });

  const data = useMemo(() => {
    if (!apps?.data && initialData?.length > 0) {
      return { data: { Items: initialData } };
    }
    if (apps?.data && initialData?.length > 0) {
      const combinedItems = [
        ...initialData,
        ...apps.data.Items.filter(
          (app) => !initialData.some((initial) => initial[KEY] === app[KEY]),
        ),
      ];
      return { data: { Items: combinedItems } };
    }
    return apps;
  }, [apps, initialData]);

  const id = useId();

  const onInputChange = debounce((e, value) => {
    if (e?.type === 'change') {
      if (isStaticOptions) {
        setSearchTermStatic(value);
      } else {
        setSearchTerm(value);
      }
    }
  }, 500);

  const onOpen = () => {
    setIsEnabled(true);
  };

  return (
    <Autocomplete
      limitTags={limitTags}
      fullWidth
      multiple={isMultiple}
      disableCloseOnSelect={isMultiple}
      value={value}
      id={`${FIELD_NAME}-select-${id}`}
      name={FIELD_NAME}
      options={
        isStaticOptions
          ? currentOptionsStatic :
          data?.data?.Items?.filter(
            (e) => !excludes?.some((f) => f[KEY] === e[KEY]),
          ) ?? []
      }
      getOptionLabel={(option) => option[LABEL]}
      onInputChange={onInputChange}
      onChange={(e, value) => {
        setSearchTerm('');
        rest?.onChange?.(e, value);
      }}
      onOpen={onOpen}
      isOptionEqualToValue={(option, value) => {
        return option?.[KEY] === value?.[KEY];
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        return (
          <Box
            {...optionProps}
            key={option?.[KEY]}
            sx={{ '& .MuiAvatar-root': { mr: 2, border: '1px solid #ddd' } }}
          >
            {isMultiple && (
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
            )}

            <Avatar alt={option[LABEL]} src={option.IconBlobReference}></Avatar>
            {option[LABEL]}
          </Box>
        );
      }}
      renderTags={(value, getTagProps) => {
        return value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option[KEY]}
            label={option[LABEL]}
          />
        ));
      }}
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={
            isMultiple ? 'Select Marketplace App' : 'Select Marketplace Apps'
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading ? (
                  <CircularProgress color='inherit' size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          {...textFieldProps}
        />
      )}
      {...rest}
    />
  );
};

export default React.memo(MarketplaceAppSelect);

MarketplaceAppSelect.propTypes = {
  error: PropTypes.bool,
  helperText: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  required: PropTypes.bool,
  handleInputChange: PropTypes.func,
  excludes: PropTypes.array,
  textFieldProps: PropTypes.object,
  isMultiple: PropTypes.bool,
  queryKey: PropTypes.string,
  limitTags: PropTypes.number,
  initialData: PropTypes.array,
  options: PropTypes.array,
};
